import React, { FC, ChangeEvent, useState, useEffect } from 'react';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import { Box } from '@mui/system';

type NumberOnlyTextField2Props = {
    hasDecimal?: boolean;
    showStepper?: boolean;
    allowNegative?: boolean;
    onlyAllowNegative?: boolean;
    maxLength?: number;
    onValueChange?: (value: number) => void;
} & TextFieldProps;

const NumberOnlyTextField2: FC<NumberOnlyTextField2Props> = ({
    label,
    onValueChange,
    allowNegative = false,
    onlyAllowNegative = false,
    hasDecimal = true,
    showStepper = false,
    maxLength = Number.MAX_SAFE_INTEGER,
    ...props
}: NumberOnlyTextField2Props) => {
    const [displayValue, setDisplayValue] = useState<string>('');
    let regexStr = hasDecimal ? '[0-9]*[,.]?[0-9]*$' : '[0-9]*$';
    regexStr = onlyAllowNegative ? `^-${regexStr}` : allowNegative ? `^-?${regexStr}` : `^${regexStr}`;
    const numRegex = new RegExp(regexStr);
    const { value: _, ...remainingProps } = props;
    const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        if (!numRegex.test(value) || value.length > maxLength) {
            return event.preventDefault();
        }

        setDisplayValue(value);
        if (onValueChange && value && value !== '-') {
            const numericValue = value ? parseFloat(value.replace(',', '.')) : 0;
            onValueChange(numericValue);
        }
    };

    useEffect(() => {
        if (!Number.isNaN(props.value)) {
            setDisplayValue((props.value as number).toString().replace('.', ','));
            return;
        }
        if (!props.value) {
            setDisplayValue('');
            return;
        }
        setDisplayValue(props.value as string);
    }, [props.value]);

    const handleStepper = (step: number) => {
        const currentValue = displayValue ? parseInt(displayValue, 10) : 0;
        let newValue = currentValue + step;
        if (!allowNegative && newValue < 0) newValue = 0;
        setDisplayValue(newValue.toString());
        if (onValueChange) onValueChange(newValue);
    };

    return (
        <Box display="flex" alignItems="center">
            <TextField
                label={label}
                onChange={onInputChange}
                {...remainingProps}
                value={displayValue}
                autoComplete="off"
                type="text"
                margin="dense"
            />
            {showStepper && !hasDecimal && (
                <>
                    <IconButton
                        aria-label="increase"
                        onClick={() => handleStepper(1)}
                        size="small"
                        sx={{ p: 0.5, ml: 0.5 }}
                    >
                        <AddIcon fontSize="inherit" sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton
                        aria-label="decrease"
                        onClick={() => handleStepper(-1)}
                        size="small"
                        sx={{ p: 0.5, ml: 0.5 }}
                    >
                        <RemoveIcon fontSize="inherit" sx={{ fontSize: 16 }} />
                    </IconButton>
                </>
            )}
        </Box>
    );
};

export default NumberOnlyTextField2;
